<template>
  <div class="wealth-home">
    <header class="hero">
      <div class="hero-top">
        <div class="brand">
          <span class="brand-mark">ASM</span>
          <div>
            <strong>ASM太平洋</strong>
            <small>全球资产配置服务平台</small>
          </div>
        </div>
        <button class="message-btn" @click="go('/article')" aria-label="活动公告">
          <span>◌</span>
          <i></i>
        </button>
      </div>

      <section
        class="hero-intro milestone-carousel"
        @mouseenter="stopMilestoneAutoplay"
        @mouseleave="startMilestoneAutoplay"
        @touchstart.passive="onMilestoneTouchStart"
        @touchend.passive="onMilestoneTouchEnd"
      >
        <div
          class="milestone-track"
          :style="{ transform: 'translate3d(-' + milestoneIndex * 100 + '%, 0, 0)' }"
        >
          <article
            v-for="slide in milestoneSlides"
            :key="slide.year"
            class="milestone-slide"
            :style="{ backgroundImage: 'url(' + slide.image + ')' }"
            @click="go(slide.path)"
          >
            <div class="milestone-shade"></div>
            <div class="milestone-copy">
              <span>{{ slide.year }} · {{ slide.english }}</span>
              <h1>{{ slide.title }}</h1>
              <p>{{ slide.description }}</p>
              <button @click.stop="go(slide.path)">查看里程碑</button>
            </div>
          </article>
        </div>
        <div class="milestone-dots" aria-label="公司里程碑轮播">
          <button
            v-for="(slide, index) in milestoneSlides"
            :key="slide.year + '-dot'"
            :class="{ active: milestoneIndex === index }"
            :aria-label="'查看' + slide.year + '里程碑'"
            @click.stop="setMilestone(index)"
          ></button>
        </div>
      </section>
    </header>

    <main class="home-main">
      <section class="notice-strip" @click="go('/news')">
        <span class="notice-tag">公告</span>
        <p>{{ noticeTitle }}</p>
        <b>›</b>
      </section>

      <section class="benefit-grid">
        <article class="checkin-card" @click="go('/Signln')">
          <div>
            <span class="eyebrow">DAILY BENEFITS</span>
            <h3>每日签到</h3>
            <p>连续签到 7 天，积分奖励加倍</p>
          </div>
          <div class="calendar-art">
            <small>今日</small>
            <strong>{{ today }}</strong>
          </div>
        </article>
        <article class="newcomer-card" @click="go('/newcomer-gift')">
          <span class="eyebrow">NEW MEMBER</span>
          <h3>新客专享礼</h3>
          <p>查看新客礼物、领取条件与权益说明</p>
          <span class="text-link">查看礼遇 →</span>
        </article>
      </section>

      <section class="lottery-card">
        <div class="lottery-copy">
          <span class="eyebrow light">MEMBER ACTIVITY</span>
          <h2>幸运大转盘</h2>
          <p>{{ lotteryRule }}</p>
          <button @click="go('/user/zhuanpan')">立即抽奖</button>
        </div>
        <div class="wheel-wrap" @click="go('/user/zhuanpan')">
          <div class="wheel">
            <div class="wheel-center">GO</div>
          </div>
          <div class="wheel-glow"></div>
        </div>
      </section>

      <section class="section">
        <div class="section-title">
          <div>
            <span class="eyebrow">SELECTED PRODUCTS</span>
            <h2>热门产品</h2>
          </div>
          <button @click="go('/category')">产品专区</button>
        </div>
        <div class="product-list">
          <article
            v-for="(product, index) in displayProducts"
            :key="product.id"
            class="product-card"
            :class="'product-tone-' + index"
            @click="openProduct(product)"
          >
            <div class="product-top">
              <span>{{ product.tag }}</span>
              <small>0{{ index + 1 }}</small>
            </div>
            <h3>{{ product.title }}</h3>
            <div class="product-rate">
              <strong>{{ product.rate }}</strong>
              <span>业绩比较基准</span>
            </div>
            <div class="product-meta">
              <p><span>期限</span><strong>{{ product.term }}</strong></p>
              <p><span>起投</span><strong>{{ shortMin(product.min) }}</strong></p>
            </div>
            <div class="product-level">查看详情 <b>→</b></div>
          </article>
        </div>
      </section>

      <section class="section">
        <div class="section-title">
          <div>
            <span class="eyebrow">INSIGHTS & EDUCATION</span>
            <h2>资讯与产品指南</h2>
          </div>
          <button @click="go('/knowledge')">进入专区</button>
        </div>
        <div class="knowledge-grid">
          <button
            v-for="item in knowledgeEntries"
            :key="item.title"
            @click="go(item.path)"
          >
            <span :class="item.tone">{{ item.icon }}</span>
            <strong>{{ item.title }}</strong>
            <small>{{ item.desc }}</small>
          </button>
        </div>
        <article
          v-for="(news, index) in displayNews"
          :key="news.id || index"
          class="news-row"
          @click="openNews(news)"
        >
          <div class="news-cover" :class="'cover-' + index">
            <span>{{ news.category }}</span>
          </div>
          <div>
            <h3>{{ news.title }}</h3>
            <p>{{ news.summary }}</p>
            <small>{{ news.date }} · {{ news.readTime }}</small>
          </div>
        </article>
      </section>

      <section class="company-entry" @click="go('/company-hub')">
        <div>
          <span class="eyebrow light">ABOUT ASM PACIFIC</span>
          <h2>认识 ASM太平洋，安心开启财富规划</h2>
          <p>公司介绍 · 项目白皮书 · 资质与合规 · 新手指引</p>
        </div>
        <span class="company-arrow">→</span>
      </section>

    </main>
  </div>
</template>

<script>
import request from "@utils/request";
import { getCategory } from "@api/store";

const FALLBACK_PRODUCTS = [
  { id: "stable", tag: "定返分红型", title: "稳享定期返利计划", rate: "5.80%", term: "180天", min: "100 USD", level: "按约返利", fallback: true },
  { id: "growth", tag: "到期本息型", title: "优选到期返本返息计划", rate: "8.60%", term: "365天", min: "500 USD", level: "到期结算", fallback: true },
  { id: "income", tag: "按日返息型", title: "灵活按日返息计划", rate: "3.60%", term: "90天", min: "50 USD", level: "按日返息", fallback: true }
];

const FALLBACK_NEWS = [
  { id: "market", category: "市场观察", title: "全球资产配置：如何在波动中保持投资纪律", summary: "从资产相关性、投资期限和再平衡机制出发，理解组合配置的核心逻辑。", date: "07-26", readTime: "6分钟", fallback: true },
  { id: "report", category: "研报解读", title: "下半年宏观展望：增长、利率与市场机会", summary: "梳理影响全球市场的主要变量，帮助投资者建立清晰的观察框架。", date: "07-24", readTime: "8分钟", fallback: true }
];

export default {
  name: "Index",
  data() {
    return {
      noticeTitle: "关于平台服务升级及投资者适当性管理的公告",
      milestoneIndex: 0,
      milestoneTimer: null,
      milestoneTouchStartX: 0,
      milestoneSlides: [
        {
          year: "1975",
          english: "FOUNDATION",
          title: "扎根香港，开启半导体征程",
          description: "ASM太平洋在香港成立，作为荷兰ASM International的亚洲营销部门，开启精密制造之路。",
          image: require("@/assets/images/milestones/milestone-victoria-harbour.webp"),
          path: "/company-profile/company"
        },
        {
          year: "2002",
          english: "GLOBAL NO.1",
          title: "登顶全球，引领封装技术",
          description: "成为全球排名第一的半导体组装与封装设备制造商，持续推动先进封装技术创新。",
          image: require("@/assets/images/milestones/milestone-technology.jpg"),
          path: "/company-profile/project"
        },
        {
          year: "2011",
          english: "STRATEGIC EXPANSION",
          title: "战略收购，构建全产业链",
          description: "收购德国西门子SIPLACE业务进军SMT领域，新加坡成为全球总部，开启多元布局。",
          image: require("@/assets/images/milestones/milestone-global.jpg"),
          path: "/company-profile/company"
        },
        {
          year: "2025",
          english: "50TH ANNIVERSARY",
          title: "五十周年，赋能数字世界",
          description: "庆祝成立50周年，持续引领半导体与电子制造技术创新，共建可持续未来。",
          image: require("@/assets/images/milestones/milestone-asmpt-hong-kong-building.webp"),
          path: "/company-hub"
        }
      ],
      today: new Date().getDate(),
      products: [],
      newsList: [],
      wheelPrizes: ["10积分", "5 USD", "加息券", "精美礼品", "50积分", "20积分"],
      lotteryRule: "每充值500 USD获得1次机会，现金礼金可直接提现",
      knowledgeEntries: [
        { title: "财经新闻", desc: "市场热点速递", icon: "闻", tone: "blue-soft", path: "/knowledge?tab=news" },
        { title: "研报解读", desc: "专业观点精读", icon: "研", tone: "purple-soft", path: "/knowledge?tab=report" },
        { title: "投资课程", desc: "体系化学投资", icon: "学", tone: "green-soft", path: "/knowledge?tab=course" },
        { title: "风险揭示", desc: "投资前必读", icon: "险", tone: "orange-soft", path: "/knowledge?tab=risk" }
      ]
    };
  },
  computed: {
    displayProducts() {
      return this.products.length ? this.products.slice(0, 3) : FALLBACK_PRODUCTS;
    },
    displayNews() {
      return this.newsList.length ? this.newsList.slice(0, 2) : FALLBACK_NEWS;
    }
  },
  mounted() {
    this.loadPageData();
    this.startMilestoneAutoplay();
  },
  beforeDestroy() {
    this.stopMilestoneAutoplay();
  },
  methods: {
    go(path) {
      this.$router.push(path);
    },
    setMilestone(index) {
      this.milestoneIndex = index;
      this.startMilestoneAutoplay();
    },
    nextMilestone() {
      this.milestoneIndex =
        (this.milestoneIndex + 1) % this.milestoneSlides.length;
    },
    startMilestoneAutoplay() {
      this.stopMilestoneAutoplay();
      this.milestoneTimer = window.setInterval(this.nextMilestone, 4500);
    },
    stopMilestoneAutoplay() {
      if (this.milestoneTimer) {
        window.clearInterval(this.milestoneTimer);
        this.milestoneTimer = null;
      }
    },
    onMilestoneTouchStart(event) {
      this.stopMilestoneAutoplay();
      this.milestoneTouchStartX =
        event.touches && event.touches[0] ? event.touches[0].clientX : 0;
    },
    onMilestoneTouchEnd(event) {
      const endX =
        event.changedTouches && event.changedTouches[0]
          ? event.changedTouches[0].clientX
          : this.milestoneTouchStartX;
      const distance = endX - this.milestoneTouchStartX;
      if (Math.abs(distance) > 40) {
        const direction = distance < 0 ? 1 : -1;
        this.milestoneIndex =
          (this.milestoneIndex + direction + this.milestoneSlides.length) %
          this.milestoneSlides.length;
      }
      this.startMilestoneAutoplay();
    },
    formatMoney(value) {
      const amount = Number(value || 0);
      return amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    loadPageData() {
      getCategory()
        .then(res => {
          const categories = (res && res.data && res.data.data) || [];
          const raw = categories.reduce((list, category) => list.concat(category.list || []), []);
          this.products = raw.slice(0, 3).map(item => ({
            id: item.id,
            tag: item.tag || "精选产品",
            title: item.title || "资产配置计划",
            rate: this.toPercent(item.rate),
            term: item.day ? item.day + "天" : "灵活",
            min: this.formatMoney(item.min || 0) + " USD",
            level: item.risk_name || "风险等级详见产品"
          }));
        })
        .catch(() => {});

      request
        .post("/api/v1/article/cat/all", {}, { login: false })
        .then(res => {
          const groups = (res && res.data && res.data.data) || [];
          const articles = groups.reduce((list, group) => list.concat(group.list || []), []);
          this.newsList = articles.slice(0, 2).map(item => ({
            id: item.id,
            category: item.cate_name || "财富洞察",
            title: item.title,
            summary: (item.synopsis || "关注市场变化，了解专业财富管理观点。").replace(/<[^>]+>/g, ""),
            date: item.add_time ? String(item.add_time).slice(5, 10) : "最新",
            readTime: "5分钟"
          }));
        })
        .catch(() => {});

      request
        .post("/api/v1/article/article/list", { cid: 7, page: 1 }, { login: false })
        .then(res => {
          const notices = (res && res.data && res.data.data) || [];
          if (notices[0] && notices[0].title) this.noticeTitle = notices[0].title;
        })
        .catch(() => {});

      request
        .get("/user/user/zpanindex")
        .then(res => {
          const lottery = (res && res.data && res.data.data) || {};
          if (Array.isArray(lottery.zhpan) && lottery.zhpan.length) {
            this.wheelPrizes = lottery.zhpan.slice(0, 8).map(item => {
              return `${item.name}${item.num ? " " + item.num : ""}`;
            });
          }
          if (lottery.rule) this.lotteryRule = lottery.rule;
        })
        .catch(() => {});
    },
    toPercent(value) {
      const number = Number(value);
      if (Number.isNaN(number)) return value || "--";
      return number.toFixed(2) + "%";
    },
    openProduct(product) {
      this.go(product.fallback ? "/category" : "/detail/" + product.id);
    },
    shortMin(value) {
      return String(value || "--").replace(".00 USD", "").replace(" USD", "");
    },
    openNews(news) {
      this.go(news.fallback ? "/knowledge" : "/article_detail?id=" + news.id);
    }
  }
};
</script>

<style scoped lang="less">
.wealth-home {
  width: 100%;
  max-width: 100%;
  min-height: 100vh;
  padding-bottom: 76px;
  overflow-x: hidden;
  color: #17243d;
  background: #f4f7fb;
}

.wealth-home,
.wealth-home * {
  box-sizing: border-box;
}

button {
  font: inherit;
  border: 0;
  background: none;
}

.hero {
  padding: 18px 16px 27px;
  color: #fff;
  background:
    radial-gradient(circle at 88% 10%, rgba(90, 174, 255, 0.35), transparent 29%),
    linear-gradient(145deg, #061d4a 0%, #0a3679 58%, #1166ba 100%);
  border-radius: 0 0 30px 30px;
}

.hero-intro {
  position: relative;
  height: 202px;
  padding: 0;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 21px;
  background: #061d4a;
  box-shadow: 0 18px 45px rgba(2, 19, 54, 0.24);
}

.milestone-track {
  display: flex;
  width: 100%;
  height: 100%;
  margin: 0;
  transition: transform 0.65s cubic-bezier(0.22, 0.75, 0.28, 1);
  will-change: transform;
}

.milestone-slide {
  position: relative;
  flex: 0 0 100%;
  width: 100%;
  height: 100%;
  overflow: hidden;
  cursor: pointer;
  background-position: center center;
  background-size: cover;
}

.milestone-shade {
  position: absolute;
  z-index: 0;
  inset: 0;
  background:
    linear-gradient(90deg, rgba(3, 18, 48, 0.95) 0%, rgba(4, 28, 69, 0.8) 42%, rgba(4, 29, 69, 0.08) 78%),
    linear-gradient(0deg, rgba(3, 16, 43, 0.35), transparent 55%);
}

.milestone-copy {
  position: relative;
  z-index: 1;
  width: 72%;
  padding: 23px 20px 20px;
}

.milestone-copy > span {
  color: #9cccf4;
  font-size: 8px;
  font-weight: 700;
  letter-spacing: 1.1px;
}

.milestone-copy h1 {
  max-width: 230px;
  margin: 8px 0 7px;
  color: #fff;
  font-size: 21px;
  line-height: 1.35;
}

.milestone-copy p {
  display: -webkit-box;
  max-width: 225px;
  margin: 0;
  overflow: hidden;
  color: rgba(231, 243, 255, 0.78);
  font-size: 9px;
  line-height: 1.65;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.milestone-copy button {
  padding: 7px 12px;
  margin-top: 12px;
  color: #164c88;
  font-size: 9px;
  font-weight: 700;
  background: rgba(255, 255, 255, 0.94);
  border-radius: 15px;
}

.milestone-dots {
  position: absolute;
  z-index: 3;
  right: 14px;
  bottom: 12px;
  display: flex;
  gap: 5px;
}

.milestone-dots button {
  width: 5px;
  height: 5px;
  padding: 0;
  background: rgba(255, 255, 255, 0.44);
  border-radius: 5px;
  transition: width 0.25s ease, background 0.25s ease;
}

.milestone-dots button.active {
  width: 16px;
  background: #fff;
}

.hero-top,
.asset-head,
.asset-grid,
.section-title,
.strategy-banner,
.product-top,
.hero .primary-actions {
  display: flex;
  align-items: center;
}

.hero-top {
  justify-content: space-between;
  margin-bottom: 22px;
}

.brand {
  display: flex;
  align-items: center;
  gap: 10px;
}

.brand-mark {
  display: grid;
  width: 40px;
  height: 40px;
  place-items: center;
  color: #0d3672;
  font-size: 13px;
  font-weight: 800;
  letter-spacing: -1px;
  background: linear-gradient(145deg, #fff, #a9d9ff);
  border-radius: 13px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.18);
}

.brand strong,
.brand small {
  display: block;
}

.brand strong {
  font-size: 17px;
  letter-spacing: 1px;
}

.brand small {
  margin-top: 3px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 10px;
}

.message-btn {
  position: relative;
  width: 38px;
  height: 38px;
  color: #fff;
  font-size: 25px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
}

.message-btn i {
  position: absolute;
  top: 8px;
  right: 7px;
  width: 7px;
  height: 7px;
  background: #ffca60;
  border: 2px solid #0a3679;
  border-radius: 50%;
}

.asset-card {
  padding: 18px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.09);
  box-shadow: 0 18px 45px rgba(2, 19, 54, 0.26);
  backdrop-filter: blur(8px);
}

.asset-head {
  justify-content: space-between;
  color: rgba(255, 255, 255, 0.7);
  font-size: 12px;
}

.asset-head button {
  color: rgba(255, 255, 255, 0.7);
  font-size: 11px;
}

.asset-value {
  margin: 7px 0 17px;
  font-size: 31px;
  font-weight: 700;
  letter-spacing: 0.5px;
}

.asset-grid {
  gap: 22px;
}

.asset-grid div span,
.asset-grid div strong {
  display: block;
}

.asset-grid div span {
  color: rgba(255, 255, 255, 0.6);
  font-size: 10px;
}

.asset-grid div strong {
  margin-top: 5px;
  font-size: 13px;
}

.asset-grid .positive {
  color: #79e4bd;
}

.asset-detail {
  margin-left: auto;
  padding: 8px 10px;
  color: #d8ecff;
  font-size: 11px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.1);
}

.hero .primary-actions {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  margin: 20px 5px -52px;
  padding: 17px 11px 14px;
  color: #20304a;
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 14px 35px rgba(17, 44, 83, 0.13);
}

.primary-actions button {
  width: 100%;
  min-width: 0;
  color: #34435b;
}

.primary-actions em {
  display: block;
  margin-top: 7px;
  font-size: 11px;
  font-style: normal;
}

.action-icon {
  display: grid;
  width: 38px;
  height: 38px;
  margin: 0 auto;
  place-items: center;
  font-size: 18px;
  font-weight: 600;
  border-radius: 13px;
}

.blue { color: #1f6fea; background: #eaf2ff; }
.cyan { color: #1197a6; background: #e5f9fb; }
.violet { color: #7653d6; background: #f0ebff; }
.gold { color: #b77a17; background: #fff5df; }
.green { color: #198c69; background: #e7f8f1; }

.home-main {
  padding: 20px 14px 10px;
}

.notice-strip {
  display: flex;
  align-items: center;
  gap: 9px;
  margin-bottom: 14px;
  padding: 12px 13px;
  background: #fff;
  border-radius: 14px;
}

.notice-tag {
  padding: 3px 7px;
  color: #1762c1;
  font-size: 10px;
  font-weight: 700;
  background: #eaf2ff;
  border-radius: 6px;
}

.notice-strip p {
  flex: 1;
  overflow: hidden;
  color: #3c4b61;
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.notice-strip b {
  color: #9ba7b6;
  font-size: 19px;
}

.benefit-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.25fr) minmax(0, 1fr);
  gap: 10px;
  margin-bottom: 14px;
}

.benefit-grid article {
  min-width: 0;
  min-height: 132px;
  padding: 16px;
  border-radius: 18px;
  overflow: hidden;
}

.checkin-card {
  position: relative;
  background: linear-gradient(145deg, #e9f3ff, #f7fbff);
}

.newcomer-card {
  background: linear-gradient(145deg, #fff3dc, #fffaf1);
}

.eyebrow {
  color: #6f8db2;
  font-size: 8px;
  font-weight: 700;
  letter-spacing: 1.2px;
}

.eyebrow.light {
  color: rgba(255, 255, 255, 0.68);
}

.benefit-grid h3 {
  margin: 8px 0 6px;
  font-size: 16px;
}

.benefit-grid p {
  max-width: 120px;
  color: #6a788b;
  font-size: 10px;
  line-height: 1.6;
}

.calendar-art {
  position: absolute;
  right: 13px;
  bottom: 13px;
  width: 48px;
  height: 48px;
  text-align: center;
  background: #fff;
  border-radius: 13px;
  box-shadow: 0 8px 20px rgba(28, 91, 169, 0.12);
}

.calendar-art small,
.calendar-art strong {
  display: block;
}

.calendar-art small {
  padding: 3px;
  color: #fff;
  font-size: 8px;
  background: #397ed7;
  border-radius: 13px 13px 0 0;
}

.calendar-art strong {
  padding-top: 4px;
  color: #255b9d;
  font-size: 18px;
}

.text-link {
  display: block;
  margin-top: 13px;
  color: #a26712;
  font-size: 10px;
  font-weight: 700;
}

.lottery-card {
  position: relative;
  display: flex;
  min-height: 188px;
  margin-bottom: 14px;
  padding: 22px 17px;
  color: #fff;
  overflow: hidden;
  background:
    radial-gradient(circle at 80% 50%, rgba(255, 213, 107, 0.24), transparent 30%),
    linear-gradient(135deg, #512b8c, #7e3f9f 55%, #a34b7f);
  border-radius: 20px;
  box-shadow: 0 14px 30px rgba(78, 37, 126, 0.2);
}

.lottery-copy {
  position: relative;
  z-index: 2;
  width: 55%;
}

.lottery-copy h2 {
  margin: 8px 0 5px;
  font-size: 23px;
}

.lottery-copy p {
  margin-bottom: 16px;
  color: rgba(255, 255, 255, 0.77);
  font-size: 10px;
}

.lottery-copy button {
  display: block;
  width: 106px;
  padding: 10px;
  color: #6b397e;
  font-size: 12px;
  font-weight: 700;
  background: linear-gradient(180deg, #fff3b9, #ffc865);
  border-radius: 20px;
  box-shadow: 0 7px 16px rgba(56, 19, 74, 0.25);
}

.lottery-copy small {
  display: block;
  margin-top: 10px;
  color: rgba(255, 255, 255, 0.5);
  font-size: 7px;
}

.wheel-wrap {
  position: absolute;
  top: 15px;
  right: -14px;
  width: 175px;
  height: 175px;
}

.wheel,
.wheel-glow {
  position: absolute;
  border-radius: 50%;
}

.wheel {
  z-index: 2;
  inset: 8px;
  background: conic-gradient(
    #ffe8a7 0 45deg,
    #fff7df 45deg 90deg,
    #f6c6da 90deg 135deg,
    #fff7df 135deg 180deg,
    #ffe8a7 180deg 225deg,
    #fff7df 225deg 270deg,
    #f6c6da 270deg 315deg,
    #fff7df 315deg
  );
  border: 7px solid #f6cd62;
  box-shadow: inset 0 0 0 3px #fff2b6, 0 10px 25px rgba(48, 17, 72, 0.4);
}

.wheel::before {
  position: absolute;
  inset: 7px;
  content: "";
  border: 1px dashed rgba(112, 45, 101, 0.24);
  border-radius: 50%;
}

.wheel-center {
  position: absolute;
  top: 50%;
  left: 50%;
  display: grid;
  width: 48px;
  height: 48px;
  place-items: center;
  color: #6b397e;
  font-size: 14px;
  font-weight: 900;
  background: linear-gradient(#fff3b4, #ffc552);
  border: 5px solid #fff2b8;
  border-radius: 50%;
  transform: translate(-50%, -50%);
}

.wheel-glow {
  z-index: 1;
  inset: -8px;
  background: rgba(255, 214, 103, 0.14);
  filter: blur(7px);
}

.section {
  margin-bottom: 14px;
  padding: 18px 14px;
  background: #fff;
  border-radius: 20px;
}

.section-title {
  justify-content: space-between;
  margin-bottom: 14px;
}

.section-title h2 {
  margin-top: 3px;
  font-size: 19px;
}

.section-title button {
  color: #718098;
  font-size: 10px;
}

.strategy-banner {
  padding: 13px;
  background: linear-gradient(100deg, #edf5ff, #f8fbff);
  border: 1px solid #e3edfa;
  border-radius: 15px;
}

.strategy-icon {
  display: grid;
  width: 39px;
  height: 39px;
  margin-right: 11px;
  place-items: center;
  color: #fff;
  font-size: 22px;
  background: linear-gradient(145deg, #387fe0, #174ca4);
  border-radius: 12px;
}

.strategy-banner strong {
  font-size: 13px;
}

.strategy-banner p {
  margin-top: 4px;
  color: #7a879a;
  font-size: 9px;
}

.strategy-banner > span {
  margin-left: auto;
  color: #3373cc;
  font-size: 10px;
}

.product-list {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
}

.product-card {
  position: relative;
  min-width: 0;
  min-height: 196px;
  padding: 12px 10px;
  overflow: hidden;
  color: #fff;
  border-radius: 16px;
  box-shadow: 0 10px 22px rgba(29, 59, 104, 0.16);
}

.product-card::before {
  position: absolute;
  inset: 0;
  content: "";
  background:
    linear-gradient(180deg, rgba(7, 15, 31, 0.08) 0%, rgba(7, 15, 31, 0.48) 48%, rgba(5, 11, 23, 0.82) 100%),
    linear-gradient(90deg, rgba(5, 12, 27, 0.55) 0%, transparent 100%);
}

.product-card::after {
  position: absolute;
  right: 12px;
  bottom: 11px;
  width: 24px;
  height: 24px;
  content: "";
  background: rgba(255, 255, 255, 0.09);
  border-radius: 50%;
}

.product-tone-0 {
  background-color: #123d70;
  background-image: url("../../assets/images/product-tech/ai-chip.jpg");
  background-position: 58% center;
  background-size: cover;
}

.product-tone-1 {
  background-color: #392a67;
  background-image: url("../../assets/images/product-tech/semiconductor-wafer.jpg");
  background-position: 60% center;
  background-size: cover;
}

.product-tone-2 {
  background-color: #125a59;
  background-image: url("../../assets/images/product-tech/ai-robot.jpg");
  background-position: 52% center;
  background-size: cover;
}

.product-top {
  justify-content: space-between;
  position: relative;
  z-index: 1;
}

.product-top span {
  max-width: 74px;
  padding: 4px 6px;
  overflow: hidden;
  color: rgba(255, 255, 255, 0.92);
  font-size: 8px;
  text-overflow: ellipsis;
  white-space: nowrap;
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 6px;
}

.product-top small {
  color: rgba(255, 255, 255, 0.36);
  font-size: 14px;
  font-weight: 700;
}

.product-card h3 {
  position: relative;
  z-index: 1;
  display: -webkit-box;
  height: 39px;
  margin: 10px 0 7px;
  overflow: hidden;
  color: #fff;
  font-size: 13px;
  line-height: 1.45;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.product-rate {
  position: relative;
  z-index: 1;
}

.product-rate strong,
.product-rate span {
  display: block;
}

.product-rate strong {
  color: #ffdf92;
  font-size: 21px;
  line-height: 1.2;
}

.product-rate span {
  margin-top: 3px;
  color: rgba(255, 255, 255, 0.56);
  font-size: 8px;
}

.product-meta {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px;
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
}

.product-meta p span,
.product-meta p strong {
  display: block;
}

.product-meta p span {
  color: rgba(255, 255, 255, 0.48);
  font-size: 7px;
}

.product-meta p strong {
  margin-top: 3px;
  overflow: hidden;
  color: rgba(255, 255, 255, 0.93);
  font-size: 9px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.product-level {
  position: absolute;
  z-index: 1;
  right: 9px;
  bottom: 10px;
  left: 10px;
  color: rgba(255, 255, 255, 0.58);
  font-size: 7px;
}

.product-level b {
  float: right;
  color: rgba(255, 255, 255, 0.85);
  font-size: 10px;
}

.product-risk {
  margin-top: 10px;
  color: #a4acb7;
  font-size: 7px;
  text-align: center;
}

.knowledge-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 7px;
  margin-bottom: 16px;
}

.knowledge-grid button {
  padding: 10px 2px;
}

.knowledge-grid button > span {
  display: grid;
  width: 39px;
  height: 39px;
  margin: 0 auto 8px;
  place-items: center;
  font-size: 13px;
  font-weight: 700;
  border-radius: 13px;
}

.blue-soft { color: #2674d8; background: #e9f2ff; }
.purple-soft { color: #7450c7; background: #f0ebff; }
.green-soft { color: #188a68; background: #e6f8f1; }
.orange-soft { color: #c27824; background: #fff2de; }

.knowledge-grid strong,
.knowledge-grid small {
  display: block;
}

.knowledge-grid strong {
  color: #344359;
  font-size: 10px;
}

.knowledge-grid small {
  margin-top: 4px;
  color: #a0a9b6;
  font-size: 7px;
}

.news-row {
  display: grid;
  grid-template-columns: 84px minmax(0, 1fr);
  gap: 12px;
  padding: 12px 0;
  border-top: 1px solid #edf0f4;
}

.news-cover {
  position: relative;
  display: flex;
  height: 70px;
  align-items: flex-end;
  padding: 9px;
  overflow: hidden;
  color: #fff;
  background:
    linear-gradient(145deg, rgba(8, 31, 68, .72), rgba(30, 104, 164, .32)),
    url("../../assets/images/asm-premium/advanced-packaging-fab.webp") 72% center / cover no-repeat;
  border-radius: 11px;
}

.news-cover::before {
  position: absolute;
  top: -22px;
  right: -18px;
  width: 70px;
  height: 70px;
  content: "";
  border: 12px solid rgba(255, 255, 255, 0.14);
  border-radius: 50%;
}

.cover-1 {
  background:
    linear-gradient(145deg, rgba(23, 35, 73, .73), rgba(82, 88, 154, .28)),
    url("../../assets/images/asm-premium/global-rd-campus.webp") center / cover no-repeat;
}

.news-cover span {
  position: relative;
  z-index: 1;
  font-size: 9px;
}

.news-row h3 {
  overflow: hidden;
  color: #2a374b;
  font-size: 12px;
  line-height: 1.45;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.news-row p {
  display: -webkit-box;
  margin: 5px 0;
  overflow: hidden;
  color: #8490a0;
  font-size: 9px;
  line-height: 1.45;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.news-row small {
  color: #aab1bc;
  font-size: 8px;
}

.company-entry {
  position: relative;
  display: flex;
  align-items: center;
  min-height: 128px;
  margin-bottom: 20px;
  padding: 20px;
  color: #fff;
  overflow: hidden;
  background:
    linear-gradient(90deg, rgba(5, 28, 61, .97) 0%, rgba(7, 47, 90, .82) 58%, rgba(8, 67, 118, .48) 100%),
    url("../../assets/images/asm-premium/global-headquarters.webp") 68% center / cover no-repeat;
  border-radius: 20px;
  box-shadow: 0 14px 34px rgba(10, 47, 88, .16);
}

.company-entry::after {
  position: absolute;
  right: 18px;
  bottom: 15px;
  width: 145px;
  height: 25px;
  content: "";
  opacity: .18;
  background: url("../../assets/images/asmpt_logo.svg") right center / contain no-repeat;
  filter: brightness(0) invert(1);
  pointer-events: none;
}

.company-entry > * { position: relative; z-index: 1; }

/* Three-family palette: navy/blue, brand red, and neutral white/grey. */
.newcomer-card,
.checkin-card {
  background: linear-gradient(145deg, #eaf3fb, #f8fbfe);
  border: 1px solid #e1ebf4;
}

.text-link {
  color: #1769b0;
}

.lottery-card {
  background:
    radial-gradient(circle at 82% 50%, rgba(89, 169, 221, 0.2), transparent 32%),
    linear-gradient(135deg, #071e38, #0d4776 58%, #1769a6);
  box-shadow: 0 14px 30px rgba(8, 43, 76, 0.2);
}

.lottery-card::before {
  position: absolute;
  z-index: 0;
  inset: 0;
  content: "";
  pointer-events: none;
  opacity: 0.48;
  background:
    radial-gradient(circle, rgba(147, 218, 255, 0.7) 0 1px, transparent 1.8px) 6px 8px / 28px 28px,
    linear-gradient(rgba(106, 190, 235, 0.13) 1px, transparent 1px) 0 0 / 28px 28px,
    linear-gradient(90deg, rgba(106, 190, 235, 0.13) 1px, transparent 1px) 0 0 / 28px 28px;
  -webkit-mask-image: linear-gradient(90deg, transparent 5%, rgba(0, 0, 0, 0.22) 34%, #000 72%);
  mask-image: linear-gradient(90deg, transparent 5%, rgba(0, 0, 0, 0.22) 34%, #000 72%);
}

.lottery-card::after {
  position: absolute;
  z-index: 1;
  right: 112px;
  bottom: 13px;
  width: 115px;
  height: 72px;
  content: "";
  pointer-events: none;
  opacity: 0.54;
  background:
    radial-gradient(circle at 0 18%, #8ad8ff 0 2px, transparent 2.5px),
    radial-gradient(circle at 48% 18%, #8ad8ff 0 2px, transparent 2.5px),
    radial-gradient(circle at 48% 82%, #8ad8ff 0 2px, transparent 2.5px),
    radial-gradient(circle at 100% 82%, #8ad8ff 0 2px, transparent 2.5px),
    linear-gradient(90deg, #65bde9, #65bde9) 0 18% / 48% 1px no-repeat,
    linear-gradient(#65bde9, #65bde9) 48% 18% / 1px 64% no-repeat,
    linear-gradient(90deg, #65bde9, #65bde9) 48% 82% / 52% 1px no-repeat;
  filter: drop-shadow(0 0 5px rgba(71, 184, 239, 0.55));
}

.lottery-copy button {
  color: #123b68;
  background: #fff;
  box-shadow: 0 7px 16px rgba(5, 27, 49, 0.24);
}

.wheel {
  background: conic-gradient(
    #dcecf8 0 45deg,
    #ffffff 45deg 90deg,
    #f1d9d7 90deg 135deg,
    #ffffff 135deg 180deg,
    #dcecf8 180deg 225deg,
    #ffffff 225deg 270deg,
    #f1d9d7 270deg 315deg,
    #ffffff 315deg
  );
  border-color: #77add1;
  box-shadow:
    inset 0 0 0 3px #eef6fb,
    0 10px 25px rgba(3, 25, 47, 0.4);
}

.wheel::before {
  border-color: rgba(18, 75, 118, 0.24);
}

.wheel-center {
  color: #fff;
  background: linear-gradient(#bf4a42, #96302a);
  border-color: #f4e5e4;
}

.wheel-glow {
  background: rgba(89, 169, 221, 0.16);
}

.wheel-wrap::before,
.wheel-wrap::after {
  position: absolute;
  content: "";
  pointer-events: none;
  border-radius: 50%;
}

.wheel-wrap::before {
  z-index: 0;
  inset: -2px;
  border: 1px solid rgba(132, 211, 250, 0.25);
  box-shadow: 0 0 0 7px rgba(75, 175, 226, 0.06);
}

.wheel-wrap::after {
  z-index: 3;
  inset: 2px;
  background: conic-gradient(
    from 8deg,
    rgba(151, 222, 255, 0.78) 0 2deg,
    transparent 2deg 88deg,
    rgba(151, 222, 255, 0.78) 88deg 90deg,
    transparent 90deg 178deg,
    rgba(151, 222, 255, 0.78) 178deg 180deg,
    transparent 180deg 268deg,
    rgba(151, 222, 255, 0.78) 268deg 270deg,
    transparent 270deg
  );
  -webkit-mask: radial-gradient(circle, transparent 68%, #000 69% 71%, transparent 72%);
  mask: radial-gradient(circle, transparent 68%, #000 69% 71%, transparent 72%);
}

.product-tone-0,
.product-tone-1,
.product-tone-2 {
  background-color: #123d70;
}

.cover-1 {
  background:
    linear-gradient(145deg, rgba(8, 31, 68, .74), rgba(30, 104, 164, .3)),
    url("../../assets/images/asm-premium/global-rd-campus.webp") center / cover no-repeat;
}

.company-entry h2 {
  max-width: 245px;
  margin: 8px 0;
  font-size: 18px;
  line-height: 1.4;
}

.company-entry p {
  color: rgba(255, 255, 255, 0.66);
  font-size: 9px;
}

.company-arrow {
  display: grid;
  width: 35px;
  height: 35px;
  margin-left: auto;
  place-items: center;
  background: rgba(255, 255, 255, 0.13);
  border-radius: 50%;
}

.risk-footer {
  padding: 2px 20px 10px;
  color: #a6afba;
  font-size: 8px;
  line-height: 1.7;
  text-align: center;
}

@media (min-width: 700px) {
  .wealth-home {
    max-width: 520px;
    margin: 0 auto;
    box-shadow: 0 0 50px rgba(13, 38, 75, 0.08);
  }
}
</style>
